// ─────────────────────────────────────────────────────────────
// Centralised analytics + conversion tracking.
// All Google and Facebook events go through here so you never
// have to remember the pixel syntax inside your components.
//
// Usage:
//   const { track, conversion } = useAnalytics();
//   track.billPayment("airtime", 500);
//   conversion.signup();
// ─────────────────────────────────────────────────────────────

// ── Safe wrappers (never crash if scripts haven't loaded) ─────
const gtag  = (...args) => { try { window.gtag?.(...args);  } catch {} };
const fbq   = (...args) => { try { window.fbq?.(...args);   } catch {} };
const dl    = (event, data = {}) => {
  try { window.dataLayer?.push({ event, ...data }); } catch {}
};

// ─────────────────────────────────────────────────────────────
export function useAnalytics() {

  // ── Standard event tracker ─────────────────────────────────
  const track = {

    // User acquires a bill category page (signals intent)
    pageView: (path, title) => {
      gtag("event", "page_view", { page_path: path, page_title: title });
      dl("pageView", { pagePath: path, pageTitle: title });
    },

    // User starts filling a bill form
    beginCheckout: (service, amount) => {
      gtag("event", "begin_checkout", {
        currency: "NGN",
        value:    amount,
        items:    [{ item_name: service, price: amount, quantity: 1 }],
      });
      fbq("track", "InitiateCheckout", { currency: "NGN", value: amount, content_name: service });
      dl("beginCheckout", { service, amount });
    },

    // Successful bill payment — the core purchase event
    purchase: (service, amount, transactionId) => {
      gtag("event", "purchase", {
        transaction_id: transactionId,
        currency:       "NGN",
        value:          amount,
        items:          [{ item_name: service, price: amount, quantity: 1 }],
      });
      fbq("track", "Purchase", {
        currency:      "NGN",
        value:         amount,
        content_name:  service,
        content_type:  "product",
      });
      dl("purchase", { service, amount, transactionId });
    },

    // User taps Fund Wallet / Deposit button
    addToCart: (amount) => {
      gtag("event", "add_to_cart", { currency: "NGN", value: amount });
      fbq("track", "AddToCart", { currency: "NGN", value: amount });
    },

    // User views bill category (airtime, data, electricity, tv)
    viewItem: (service) => {
      gtag("event", "view_item", { items: [{ item_name: service }] });
      fbq("track", "ViewContent", { content_name: service });
    },

    // User opens the registration/login page
    viewSignUp: () => {
  fbq("track", "Lead", {
    currency: "NGN",
    value:    0,
  });
  dl("viewSignUp");
},
  };

  // ── Conversion events — used directly in Google Ads & Meta Ads ──
  // Each maps to a Goal/Conversion in your ad platforms.
  // Replace the google_conversion_id and label with values from
  // Google Ads → Goals → Conversion Actions.
  const conversion = {

    // User creates a new account — most valuable conversion for top-of-funnel ads
    signup: (userId) => {
      gtag("event", "conversion", {
        send_to:        "AW-XXXXXXXXX/YYYYYYYYYYY",
        value:          1.0,
        currency:       "NGN",
        transaction_id: userId,
      });

      // ✅ Add currency and value here
      fbq("track", "CompleteRegistration", {
        status:   "success",
        currency: "NGN",   // ← was missing
        value:    0,       // ← required alongside currency; use 0 if no monetary value at signup
      });
      
      dl("conversionSignup", { userId });
      console.log("[Analytics] conversion: signup", userId);
    },

    // User makes their first bill payment — retention signal
    firstPurchase: (amount, service) => {
      gtag("event", "conversion", {
        send_to:  "AW-XXXXXXXXX/ZZZZZZZZZZZ", // separate conversion label
        value:    amount,
        currency: "NGN",
      });
      fbq("track", "Purchase", {
        currency:     "NGN",
        value:        amount,
        content_name: service,
      });
      dl("conversionFirstPurchase", { amount, service });
    },

    // User funds their wallet — mid-funnel conversion
    walletFunded: (amount) => {
      gtag("event", "conversion", {
        send_to:  "AW-XXXXXXXXX/WWWWWWWWWWW",
        value:    amount,
        currency: "NGN",
      });
      fbq("track", "AddPaymentInfo", { currency: "NGN", value: amount });
      dl("conversionWalletFunded", { amount });
    },

    // User refers a friend — virality signal
    referral: () => {
      fbq("trackCustom", "ReferralShared");
      dl("conversionReferral");
    },
  };

  return { track, conversion };
}

// ── Standalone helpers (use outside React components) ─────────
// Call these in apiServices.js or after API responses.

export const Analytics = {
  purchase:      (service, amount, txId) => useAnalytics().track.purchase(service, amount, txId),
  signup:        (userId)                => useAnalytics().conversion.signup(userId),
  firstPurchase: (amount, service)       => useAnalytics().conversion.firstPurchase(amount, service),
};
